import { memo, useState, useEffect, useRef } from "react";
import { ExternalLink, Chrome, X, Loader2, AlertTriangle } from "lucide-react";
import YouTubePlayer from "@/components/YouTubePlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/*
  VideoPlayer — the ONLY thing that renders the actual media surface.
  It is deliberately isolated from chat / viewers / subtitles / room state:
  it receives ONLY `url`, a `playerRef`, and an `onAction` callback.

  It is wrapped in React.memo with a comparator that re-renders ONLY when the
  URL changes. WebSocket-driven state (chat, reactions, joins, cursors, driver,
  subtitles) NEVER reaches this component, so the iframe / YouTube player never
  reloads on those events. The only two things that change the media:
    1. the user (driver) pastes a NEW url  → memo re-renders → new surface
    2. the driver seeks                     → handled via YouTube postMessage
                                              (playerRef.applyRemote), NOT a reload
*/

const BLOCKERS = [
  "netflix.com", "google.com", "youtube.com", "facebook.com", "instagram.com",
  "twitter.com", "x.com", "amazon.com", "linkedin.com", "gmail.com", "mail.google.com",
  "chatgpt.com", "openai.com", "disneyplus.com", "hulu.com", "hbomax.com", "max.com",
  "primevideo.com", "spotify.com", "tiktok.com", "reddit.com", "accounts.google.com",
];

export function getHost(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; }
}

// Reports load status (ok / failed) to the driver exactly once when a media surface mounts.
function ReportOnMount({ onStatus, ok, reason }) {
  useEffect(() => { if (onStatus) onStatus(ok, reason); }, [onStatus, ok, reason]);
  return null;
}

export function isBlockedSite(url) {
  const h = getHost(url);
  return !!h && BLOCKERS.some((b) => h === b || h.endsWith("." + b));
}

export function isValidUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch { return false; }
}

export function extractVideoId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
}

const STREAMING = {
  netflix: { keys: ["netflix.com"], name: "Netflix", url: "https://www.netflix.com", color: "#E50914" },
  prime: { keys: ["primevideo.com", "amazon.com"], name: "Prime Video", url: "https://www.primevideo.com", color: "#00A8E1" },
  disney: { keys: ["disneyplus.com"], name: "Disney+", url: "https://www.disneyplus.com", color: "#1a3fb8" },
  hulu: { keys: ["hulu.com"], name: "Hulu", url: "https://www.hulu.com", color: "#1CE783" },
  max: { keys: ["max.com", "hbomax.com"], name: "Max", url: "https://www.max.com", color: "#3b2fd6" },
};

function streamingFor(url) {
  const h = getHost(url);
  for (const s of Object.values(STREAMING)) {
    if (s.keys.some((k) => h === k || h.endsWith("." + k))) return s;
  }
  return null;
}

function ExtensionBanner() {
  const [gone, setGone] = useState(false);
  if (gone) return null;
  return (
    <div data-testid="extension-banner" className="mt-4 w-full max-w-md mx-auto flex items-start gap-2 bg-[#0F1636] border border-white/10 rounded-xl px-3 py-2.5 text-left">
      <span className="text-base leading-5">💡</span>
      <p className="flex-1 text-[12px] text-[#A0A0AB] leading-snug">
        <span className="text-[#F1F1F5] font-semibold">Tip:</span> Install the Ibeflix Chrome Extension on desktop for automatic {"Netflix"} sync — no manual play needed!
      </p>
      <button data-testid="extension-banner-dismiss" onClick={() => setGone(true)} className="shrink-0 text-[#71717A] hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function BlockedNotice({ url }) {
  const svc = streamingFor(url);
  const [collapsed, setCollapsed] = useState(false);
  const name = svc ? svc.name : getHost(url);
  const openTarget = url || (svc ? svc.url : "#");
  const accent = svc ? svc.color : "#00D9FF";

  const OpenBtn = (
    <a
      data-testid="blocked-open-service"
      href={openTarget}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 h-11 px-6 text-white font-semibold rounded-full transition-transform hover:-translate-y-[1px] active:scale-95"
      style={{ backgroundColor: accent }}
    >
      Open {name} <ExternalLink className="w-4 h-4" />
    </a>
  );

  return (
    <div data-testid="iframe-blocked-message" className="w-full h-full overflow-y-auto flex flex-col items-center justify-center text-center gap-3 px-5 py-8 bg-gradient-to-b from-[#0A0E27] to-[#060817]">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl" style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}>
        🎬
      </div>

      {svc && !collapsed ? (
        <div className="w-full max-w-md">
          <h2 className="text-[#F1F1F5] font-bold text-xl">Watch {name} Together 🎬</h2>
          <p className="text-[#A0A0AB] text-sm mt-1">Everyone needs their own {name} account. Here's how to sync up:</p>
          <div className="mt-4 space-y-2.5 text-left">
            {[
              ["👆", "Step 1", `You open ${name} and find your show`],
              ["📋", "Step 2", "Copy the link and paste it in the bar above"],
              ["✅", "Step 3", `Everyone in the room opens the same link in their own ${name} account`],
              ["🎬", "Step 4", "Hit the SYNC button — Ibeflix counts down 3…2…1…PLAY for everyone!"],
            ].map(([icon, step, text]) => (
              <div key={step} className="flex items-start gap-3 bg-[#0F1636] border border-white/10 rounded-xl px-3 py-2.5">
                <span className="text-lg leading-6">{icon}</span>
                <div>
                  <p className="text-[#F1F1F5] text-sm font-semibold">{step}</p>
                  <p className="text-[#A0A0AB] text-[13px] leading-snug">{text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
            {OpenBtn}
            <button
              data-testid="blocked-got-it"
              onClick={() => setCollapsed(true)}
              className="h-11 px-5 bg-white/5 border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 transition-colors"
            >
              Got it, I'll paste the link
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-md">
          <p className="text-[#F1F1F5] font-semibold text-lg">Open {name} in a new tab to watch.</p>
          <p className="text-[#A0A0AB] text-sm mt-1">Use the <span className="text-[#00D9FF] font-semibold">SYNC</span> button to play together! 🎬</p>
          <div className="mt-4 flex justify-center">{OpenBtn}</div>
          {svc && (
            <button onClick={() => setCollapsed(false)} className="mt-3 text-xs text-[#71717A] hover:text-white underline">Show the step-by-step guide</button>
          )}
        </div>
      )}

      <div className="w-full max-w-md">
        <ExtensionBanner />
        <a
          data-testid="download-extension-link"
          href="/ibeflix-extension.zip"
          download
          className="mt-2 inline-flex items-center gap-2 text-xs text-[#71717A] hover:text-[#A0A0AB]"
        >
          <Chrome className="w-3.5 h-3.5" /> Download the Chrome Extension
        </a>
      </div>
    </div>
  );
}

function ExternalIframe({ url, onStatus }) {
  const [status, setStatus] = useState("checking"); // checking | loading | slow | loaded | error
  const [reloadKey, setReloadKey] = useState(0);
  const loadedRef = useRef(false);
  const valid = isValidUrl(url);

  useEffect(() => {
    if (!onStatus) return;
    if (status === "loaded") onStatus(true, "iframe");
    else if (status === "error") onStatus(false, "cannot-embed");
  }, [status, onStatus]);

  useEffect(() => {
    if (!valid) return undefined;
    let cancelled = false;
    let t5;
    let t10;
    loadedRef.current = false;
    setStatus("checking");

    const startIframeTimers = () => {
      t5 = setTimeout(() => { if (!loadedRef.current && !cancelled) setStatus("slow"); }, 5000);
      t10 = setTimeout(() => {
        if (!loadedRef.current && !cancelled) {
          setStatus("error");
          console.error("[Ibeflix] iframe load failed", { url, host: getHost(url), reason: "timeout after 10s", at: new Date().toISOString() });
        }
      }, 10000);
    };

    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/embed-check?url=${encodeURIComponent(url)}`);
        const data = await r.json();
        if (cancelled) return;
        if (data && data.embeddable === false) {
          console.error("[Ibeflix] site cannot be embedded", { url, host: getHost(url), reason: data.reason, at: new Date().toISOString() });
          setStatus("error");
          return;
        }
      } catch (e) {
        console.warn("[Ibeflix] embed-check failed, attempting to embed anyway", { url, at: new Date().toISOString() });
      }
      if (cancelled) return;
      setStatus("loading");
      startIframeTimers();
    })();

    return () => { cancelled = true; clearTimeout(t5); clearTimeout(t10); };
  }, [url, valid, reloadKey]);

  if (!valid) {
    console.error("[Ibeflix] invalid URL pasted", { url, at: new Date().toISOString() });
    return (
      <div data-testid="invalid-url-message" className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#060817]">
        <AlertTriangle className="w-10 h-10 text-[#FF3B5C]" style={{ filter: "drop-shadow(0 0 10px rgba(255,0,64,0.7))" }} />
        <p className="text-[#F1F1F5] font-semibold text-lg">❌ Invalid URL</p>
        <p className="text-[#A0A0AB] text-sm max-w-sm">Please paste a valid YouTube, Tubi, or Pluto TV link — it should start with <span className="text-[#F1F1F5] font-semibold">https://</span></p>
      </div>
    );
  }

  const onLoad = () => { loadedRef.current = true; setStatus("loaded"); };
  const onError = () => {
    console.error("[Ibeflix] iframe onError", { url, host: getHost(url), at: new Date().toISOString() });
    setStatus("error");
  };
  const retry = () => { loadedRef.current = false; setStatus("checking"); setReloadKey((k) => k + 1); };

  const showIframe = status === "loading" || status === "slow" || status === "loaded";
  const showLoader = status === "checking" || status === "loading" || status === "slow";

  return (
    <div className="w-full h-full relative bg-[#060817]">
      {showIframe && (
        <iframe
          key={`${url}-${reloadKey}`}
          data-testid="browse-iframe"
          src={url}
          title="Browse Together"
          onLoad={onLoad}
          onError={onError}
          className="w-full h-full border-0 bg-[#060817]"
          style={{ visibility: status === "loaded" ? "visible" : "hidden", pointerEvents: "auto" }}
          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
          referrerPolicy="no-referrer"
        />
      )}
      {showLoader && (
        <div data-testid="iframe-loading" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#060817] pointer-events-none">
          <Loader2 className="w-9 h-9 text-[#00D9FF] animate-spin" />
          <p className="text-[#F1F1F5] text-sm font-medium">
            {status === "slow" ? "⏳ Still loading… (slow connection?)" : "⏳ Loading video…"}
          </p>
          <p className="text-[#71717A] text-xs truncate max-w-[80%]">{getHost(url)}</p>
        </div>
      )}
      {status === "error" && (
        <div data-testid="iframe-error" className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#060817]">
          <AlertTriangle className="w-10 h-10 text-[#FF3B5C]" style={{ filter: "drop-shadow(0 0 10px rgba(255,0,64,0.7))" }} />
          <p className="text-[#F1F1F5] font-semibold text-lg">❌ Video failed to load</p>
          <p className="text-[#A0A0AB] text-sm max-w-sm">
            <span className="text-[#F1F1F5] font-semibold">{getHost(url)}</span> can't be embedded here (the site blocks it) or took too long. Open it in a new tab and hit <span className="text-[#00D9FF] font-semibold">SYNC</span>, or try a different link.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <a data-testid="iframe-error-open" href={url} target="_blank" rel="noopener noreferrer" className="btn-neon glow-cyan flex items-center justify-center gap-2 h-11 px-6 bg-[#00D9FF] text-navy-ink font-bold rounded-full hover:bg-[#33E4FF] active:scale-95">
              Open in new tab <ExternalLink className="w-4 h-4" />
            </a>
            <button data-testid="iframe-error-retry" onClick={retry} className="btn-neon h-11 px-5 bg-white/5 border border-[#00D9FF]/30 text-[#F0F6FF] font-semibold rounded-full hover:border-[#00D9FF] hover:text-[#00D9FF] transition-colors">
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const VideoPlayer = memo(
  function VideoPlayer({ url, playerRef, onAction, onStatus }) {
    const yt = extractVideoId(url);
    if (yt) {
      return (
        <div className="w-full h-full">
          <ReportOnMount key={url} onStatus={onStatus} ok reason="youtube" />
          <YouTubePlayer ref={playerRef} videoId={yt} onAction={onAction} />
        </div>
      );
    }
    if (isBlockedSite(url)) {
      return (
        <>
          <ReportOnMount key={url} onStatus={onStatus} ok={false} reason="blocked-site" />
          <BlockedNotice url={url} />
        </>
      );
    }
    return <ExternalIframe url={url} onStatus={onStatus} />;
  },
  (prev, next) => prev.url === next.url,
);

export default VideoPlayer;
